import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetArticlesQuery,
  useGetTagsQuery,
  useGetTrendingQuery,
} from '@/services/newsApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setArticleSlugs, setCategory, setContentType } from '@/store/news.slice';
import { ArticleFeedCard } from '@/components/news/ArticleFeedCard';
import { CategoryTabs } from '@/components/news/CategoryTabs';
import { FeaturedCarousel } from '@/components/news/FeaturedCarousel';
import { FeedSkeleton } from '@/components/news/FeedSkeleton';
import { FilterTabs } from '@/components/news/FilterTabs';
import { TrendingSection } from '@/components/news/TrendingSection';
import type { ArticleSummary, NewsContentType } from '@/types/newsService';

const STATIC_CONTENT_TYPES: NewsContentType[] = [
  'news',
  'analysis',
  'opinion',
  'guide',
  'recap',
  'interview',
];

/**
 * News feed screen — ported from mobile-app's `feat/news-revised` index.
 *
 * Differences from mobile:
 *  - No ChooseGame action sheet (TMA doesn't ship the GameHeader on this
 *    route). The "category" pill row replaces the game selector by letting
 *    the user filter to any qnews category tag directly.
 *  - Infinite scroll via IntersectionObserver on a sentinel div instead of
 *    FlashList's `onEndReached`.
 *  - Analytics calls dropped — TMA has no analytics layer yet.
 *  - Pull-to-refresh handled by Telegram's WebView viewport gestures.
 */
export function News() {
  const { t, i18n } = useTranslation('news');
  const dispatch = useAppDispatch();
  const locale = i18n.language || 'en';
  const { activeContentType, activeCategorySlug } = useAppSelector((s) => s.news);

  // Categories for the top filter row.
  const { data: categoryTags = [] } = useGetTagsQuery({ type: 'category' });

  // Resolve the active category slug -> tag slug (they're already aligned in
  // qnews schema, but keep the lookup so renaming would still work).
  const categoryTagSlug = useMemo(() => {
    if (!activeCategorySlug) return undefined;
    const tag = categoryTags.find((tag) => tag.slug === activeCategorySlug);
    return tag?.slug;
  }, [activeCategorySlug, categoryTags]);

  // Pagination state.
  const [page, setPage] = useState(1);
  const [articles, setArticles] = useState<ArticleSummary[]>([]);

  const articleParams = useMemo(
    () => ({
      page,
      limit: 15,
      locale,
      sort: 'latest' as const,
      ...(categoryTagSlug ? { tags: categoryTagSlug } : {}),
      ...(activeContentType ? { contentType: activeContentType } : {}),
    }),
    [page, locale, categoryTagSlug, activeContentType],
  );

  const {
    data: articlesResult,
    isFetching,
    isLoading,
    isError,
    refetch: refetchArticles,
  } = useGetArticlesQuery(articleParams);

  const hasNextPage = articlesResult?.hasNextPage ?? false;

  // Featured articles, scoped to category when active.
  const featuredParams = useMemo(
    () => ({
      featured: 'true',
      limit: 5,
      locale,
      ...(categoryTagSlug ? { tags: categoryTagSlug } : {}),
    }),
    [locale, categoryTagSlug],
  );
  const { data: featuredResult } = useGetArticlesQuery(featuredParams);
  const featuredArticles = featuredResult?.docs ?? [];

  // Trending articles, also scoped.
  const trendingParams = useMemo(
    () => ({
      limit: 5,
      period: '24h' as const,
      locale,
      ...(categoryTagSlug ? { tags: categoryTagSlug } : {}),
    }),
    [locale, categoryTagSlug],
  );
  const { data: trendingArticles = [] } = useGetTrendingQuery(trendingParams);

  const availableContentTypes = useMemo(() => {
    if (articles.length === 0) return STATIC_CONTENT_TYPES;
    const present = new Set(articles.map((a) => a.contentType));
    return STATIC_CONTENT_TYPES.filter((t) => present.has(t));
  }, [articles]);

  // Accumulate articles for infinite scroll.
  useEffect(() => {
    if (!articlesResult) return;
    setArticles((prev) =>
      page === 1 ? articlesResult.docs : [...prev, ...articlesResult.docs],
    );
  }, [articlesResult, page]);

  // Sync slug list to the slice (used by `ArticleDetail` for swipe nav).
  useEffect(() => {
    dispatch(setArticleSlugs(articles.map((a) => a.slug)));
  }, [articles, dispatch]);

  // Reset page on filter change.
  useEffect(() => {
    setPage(1);
  }, [activeContentType, categoryTagSlug]);

  const handleContentTypeChange = useCallback(
    (type: NewsContentType | null) => dispatch(setContentType(type)),
    [dispatch],
  );

  // Infinite-scroll sentinel: IntersectionObserver on the last visible div.
  useEffect(() => {
    if (!hasNextPage || isFetching) return;
    const sentinel = document.getElementById('news-feed-sentinel');
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setPage((p) => p + 1);
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, articles.length]);

  return (
    <div className="flex flex-col pb-6 pt-2">
      <header className="mb-2 px-4">
        <h1 className="text-2xl font-bold text-fg">
          {t('listPage.title', { defaultValue: 'News' })}
        </h1>
      </header>

      <CategoryTabs
        categories={categoryTags}
        activeSlug={activeCategorySlug}
        onSelect={(slug) => dispatch(setCategory(slug))}
      />

      {featuredArticles.length > 0 && (
        <FeaturedCarousel articles={featuredArticles} locale={locale} />
      )}

      {trendingArticles.length > 0 && (
        <TrendingSection articles={trendingArticles} locale={locale} />
      )}

      <FilterTabs
        activeContentType={activeContentType}
        onContentTypeChange={handleContentTypeChange}
        availableContentTypes={availableContentTypes}
      />

      {/* Body */}
      {isLoading && articles.length === 0 ? (
        <FeedSkeleton />
      ) : isError && articles.length === 0 ? (
        <FeedError onRetry={() => void refetchArticles()} />
      ) : articles.length === 0 ? (
        <EmptyState hasGameFilter={categoryTagSlug !== undefined} />
      ) : (
        // pt-5 e non pt-2: le schede sottolineate finiscono con una riga
        // sottile, e una card che parte subito sotto sembra attaccata a quella
        // riga invece che esserne il contenuto. Il respiro separa il comando
        // dal risultato.
        <div className="pt-5">
          {articles.map((article) => (
            <ArticleFeedCard key={article._id} article={article} locale={locale} />
          ))}
          <div id="news-feed-sentinel" className="h-6" />
          {isFetching && hasNextPage && articles.length > 0 && (
            <div className="flex justify-center py-4">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-brand" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FeedError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('news');
  return (
    <div className="flex flex-col items-center gap-3 px-8 py-16">
      <p className="text-center text-base text-fg-muted">
        {t('listPage.errorTitle', { defaultValue: "Couldn't load news" })}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-full bg-bg-secondary px-6 py-3 text-sm font-semibold text-brand transition-opacity active:opacity-80"
      >
        {t('listPage.errorRetry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

function EmptyState({ hasGameFilter }: { hasGameFilter: boolean }) {
  const { t } = useTranslation('news');
  return (
    <div className="flex flex-col items-center px-8 py-16">
      <p className="text-center text-base text-fg-muted">
        {hasGameFilter
          ? t('listPage.emptyForGame', {
              defaultValue: 'No news available for selected game',
            })
          : t('listPage.empty', { defaultValue: 'No news available' })}
      </p>
    </div>
  );
}

